import React, { useState } from 'react';
import Icon from './Icon';
import { useOrion } from '../context/OrionContext';
import { formalizarRapido } from '../services/formalizacaoRapida';
import { copiarTexto } from '../services/plataforma';

// ─────────────────────────────────────────────────────────────────────────────
// Formalização rápida.
//
// Deliberadamente pequena: uma caixa para escrever, um botão para formalizar,
// o resultado e o "copiar". Nada de ticket, tipo de atendimento, anexos ou
// classificação — quem precisa disso usa a formalização tradicional.
//
// A regra de ouro aqui é não crescer. Cada campo novo aproxima esta ferramenta
// do fluxo completo e apaga o motivo de ela existir.
//
// DUAS CASCAS, UM MIOLO. A ferramenta é a mesma para todo mundo, mas o lugar
// dela muda com o setor:
//
//   · suporte  → um modal, aberto pelo atalho na tela de Formalização. Lá ela é
//     a exceção: o caminho da casa é o fluxo completo, com ticket;
//   · demais setores → uma tela do menu. Para quem não formaliza ticket, ela
//     não é exceção nenhuma — é a única formalização que existe.
//
// Por isso o miolo (`useFormalizacaoRapida`) é um hook à parte: as duas cascas
// mostram exatamente a mesma coisa, e um ajuste vale para as duas.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * O miolo, como hook: devolve o corpo e as ações prontos para a casca montar.
 *
 * É hook, e não componente, porque as duas cascas precisam do MESMO estado em
 * lugares diferentes da própria moldura — o resultado no corpo, os botões no
 * rodapé. Um componente devolveria um bloco só.
 */
export function useFormalizacaoRapida({ aoFechar = null } = {}) {
  const { operador, avisar } = useOrion();

  const [texto, setTexto] = useState('');
  const [resultado, setResultado] = useState('');
  const [formalizando, setFormalizando] = useState(false);

  const formalizar = async () => {
    if (!texto.trim() || formalizando) return;
    setFormalizando(true);
    try {
      setResultado(await formalizarRapido(texto, operador));
    } catch (err) {
      avisar(`Não foi possível formalizar: ${err.message}`, { tipo: 'erro' });
    } finally {
      setFormalizando(false);
    }
  };

  const copiar = async () => {
    const copiou = await copiarTexto(resultado).catch(() => false);
    avisar(copiou ? 'Texto copiado.' : 'Não foi possível copiar.',
      { tipo: copiou ? 'ok' : 'erro' });
  };

  // Ctrl+Enter formaliza sem tirar a mão do teclado — o mesmo gesto do envio
  // nos editores do app.
  const aoTeclar = (e) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); formalizar(); }
  };

  return {
    corpo: (
      <>
        <label className="field-label">Seu texto</label>
        <textarea
          value={texto}
          onChange={e => setTexto(e.target.value)}
          onKeyDown={aoTeclar}
          rows={8}
          autoFocus
          placeholder="Escreva do seu jeito. O Orion devolve formalizado."
        />
        <div className="campo-dica" style={{ marginTop: 6 }}>
          Ctrl+Enter para formalizar.
        </div>

        {resultado && (
          <div className="panel panel-dest d-ia" style={{ marginTop: 14, marginBottom: 0 }}>
            <label className="field-label">Formalizado</label>
            <textarea
              value={resultado}
              onChange={e => setResultado(e.target.value)}
              rows={10}
            />
            <div className="campo-dica" style={{ marginTop: 6 }}>
              Pode ajustar antes de copiar.
            </div>
          </div>
        )}
      </>
    ),
    acoes: (
      <>
        {resultado && (
          <button className="btn btn-ghost btn-sm" onClick={copiar}>
            <Icon name="copy" size={13} /> Copiar
          </button>
        )}
        {aoFechar && (
          <button className="btn btn-ghost btn-sm" onClick={aoFechar} disabled={formalizando}>
            Fechar
          </button>
        )}
        <button
          className="btn btn-primary btn-sm"
          style={{ marginLeft: 'auto' }}
          onClick={formalizar}
          disabled={formalizando || !texto.trim()}
        >
          {formalizando
            ? <Icon name="rotate-cw" size={13} className="orion-spin" />
            : <Icon name="zap" size={13} />}
          {resultado ? 'Formalizar de novo' : 'Formalizar'}
        </button>
      </>
    ),
    formalizando,
  };
}

/** A casca do suporte: modal por cima da tela de Formalização. */
export default function FormalizacaoRapida({ aoFechar }) {
  const { corpo, acoes, formalizando } = useFormalizacaoRapida({ aoFechar });

  return (
    <div className="drawer-overlay" onClick={() => !formalizando && aoFechar()}>
      <div className="modal-nx modal-largo" onClick={e => e.stopPropagation()}>
        <div className="drawer-head">
          <div className="drawer-title">
            Formalização rápida
            <span className="drawer-sub">só o texto — não registra em ticket</span>
          </div>
          <button className="drawer-close" onClick={aoFechar} disabled={formalizando}>
            <Icon name="x" size={15} />
          </button>
        </div>

        <div className="drawer-body">{corpo}</div>
        <div className="drawer-foot">{acoes}</div>
      </div>
    </div>
  );
}
